import React, { useState, useRef, useCallback, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { openTab, switchTab, closeTab, closeOtherTabs, closeAllTabs, reorderTabs, updateActiveTabPath } from "../Slices/tabSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faExternalLinkAlt, faWindowRestore, faArrowLeft, faArrowRight } from "@fortawesome/free-solid-svg-icons";

const TAB_HEIGHT = 38;

const styles = {
  container: {
    position: "fixed",
    top: 60,
    right: 0,
    height: TAB_HEIGHT,
    background: "#fff",
    borderBottom: "1px solid #e8e8e8",
    display: "flex",
    alignItems: "flex-end",
    zIndex: 1040,
    transition: "left 0.3s",
    userSelect: "none",
  },
  tabIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "16px",
    height: "16px",
    borderRadius: "50%",
    fontSize: "8px",
    color: "#999",
    cursor: "pointer",
    flexShrink: 0,
    transition: "background 0.15s, color 0.15s",
  },
  tabIconDisabled: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "16px",
    height: "16px",
    borderRadius: "50%",
    fontSize: "8px",
    color: "#ddd",
    cursor: "default",
    flexShrink: 0,
  },
  tabsScroll: {
    display: "flex",
    alignItems: "flex-end",
    flex: 1,
    overflowX: "auto",
    overflowY: "hidden",
    height: "100%",
    scrollbarWidth: "none",
  },
  tab: (isActive) => ({
    display: "flex",
    alignItems: "center",
    gap: "6px",
    height: isActive ? TAB_HEIGHT : TAB_HEIGHT - 2,
    padding: "0 12px",
    fontSize: "12px",
    fontWeight: isActive ? 600 : 400,
    color: isActive ? "#1677ff" : "#555",
    background: isActive ? "#fff" : "#f5f5f5",
    borderTop: isActive ? "2px solid #1677ff" : "2px solid transparent",
    borderLeft: "1px solid #e8e8e8",
    borderRight: "1px solid #e8e8e8",
    borderBottom: isActive ? "1px solid #fff" : "1px solid #e8e8e8",
    marginBottom: isActive ? -1 : 0,
    cursor: "pointer",
    whiteSpace: "nowrap",
    maxWidth: "220px",
    minWidth: "80px",
    position: "relative",
    flexShrink: 0,
    transition: "background 0.15s, color 0.15s",
  }),
  tabTitle: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "130px",
  },
  closeBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "16px",
    height: "16px",
    borderRadius: "50%",
    fontSize: "9px",
    color: "#999",
    flexShrink: 0,
    transition: "background 0.15s, color 0.15s",
  },
  contextMenu: {
    position: "fixed",
    background: "#fff",
    borderRadius: "8px",
    boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
    border: "1px solid #e8e8e8",
    padding: "4px 0",
    zIndex: 9999,
    minWidth: "180px",
  },
  contextItem: {
    padding: "8px 16px",
    fontSize: "12px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: "#333",
    transition: "background 0.1s",
  },
};

const TabBar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const tabs = useSelector((state) => state.tabs.tabs);
  const activeTabId = useSelector((state) => state.tabs.activeTabId);
  const [contextMenu, setContextMenu] = useState(null);
  const [hoveredClose, setHoveredClose] = useState(null);
  const [hoveredTab, setHoveredTab] = useState(null);
  const [hoveredCtx, setHoveredCtx] = useState(null);
  const dragRef = useRef(null);

  // Per-tab history: { [tabId]: { stack: [path, ...], index: number } }
  const tabHistories = useRef({});
  const isNavAction = useRef(false); // flag to skip recording during back/forward
  const prevActiveTabId = useRef(activeTabId); // track tab switches
  const [, forceUpdate] = useState(0); // trigger re-render when history changes

  // When all tabs are closed (one-by-one or close all), open My Dashboard
  useEffect(() => {
    if (tabs.length === 0) {
      dispatch(openTab({ path: "/MyDashboard", title: "My Dashboard" }));
      navigate("/MyDashboard", { replace: true });
    }
  }, [tabs.length, dispatch, navigate]);

  // Initialize history for new tabs
  useEffect(() => {
    tabs.forEach((tab) => {
      if (!tabHistories.current[tab.id]) {
        tabHistories.current[tab.id] = { stack: [tab.path], index: 0 };
      }
    });
    // Clean up closed tabs
    const tabIds = new Set(tabs.map((t) => t.id));
    Object.keys(tabHistories.current).forEach((id) => {
      if (!tabIds.has(id)) delete tabHistories.current[id];
    });
  }, [tabs]);

  // Detect tab switches — skip recording when the active tab just changed
  useEffect(() => {
    if (prevActiveTabId.current !== activeTabId) {
      prevActiveTabId.current = activeTabId;
      // Tab switched — the URL will change to match the new tab,
      // but we don't want to record that as in-tab navigation
      isNavAction.current = true;
    }
  }, [activeTabId]);

  // Track URL changes and record in active tab's history
  useEffect(() => {
    if (!activeTabId) return;
    const path = location.pathname;

    // If this navigation came from back/forward or tab switch, skip recording
    if (isNavAction.current) {
      isNavAction.current = false;
      return;
    }

    const history = tabHistories.current[activeTabId];
    if (!history) return;

    // Don't record if same as current position
    if (history.stack[history.index] === path) return;

    // Trim forward history (we navigated to a new page, discard forward stack)
    history.stack = history.stack.slice(0, history.index + 1);
    history.stack.push(path);
    history.index = history.stack.length - 1;
    forceUpdate((n) => n + 1);
  }, [location.pathname]);

  // Back: go to previous page in this tab's history
  const goBack = useCallback((tabId) => {
    const history = tabHistories.current[tabId];
    if (!history || history.index <= 0) return;
    history.index -= 1;
    const targetPath = history.stack[history.index];
    isNavAction.current = true;
    dispatch(updateActiveTabPath({ path: targetPath }));
    navigate(targetPath, { replace: true });
    forceUpdate((n) => n + 1);
  }, [dispatch, navigate]);

  // Forward: go to next page in this tab's history
  const goForward = useCallback((tabId) => {
    const history = tabHistories.current[tabId];
    if (!history || history.index >= history.stack.length - 1) return;
    history.index += 1;
    const targetPath = history.stack[history.index];
    isNavAction.current = true;
    dispatch(updateActiveTabPath({ path: targetPath }));
    navigate(targetPath, { replace: true });
    forceUpdate((n) => n + 1);
  }, [dispatch, navigate]);

  const canGoBack = (tabId) => {
    const h = tabHistories.current[tabId];
    return h && h.index > 0;
  };
  const canGoForward = (tabId) => {
    const h = tabHistories.current[tabId];
    return h && h.index < h.stack.length - 1;
  };

  const handleTabClick = useCallback((tabId) => {
    dispatch(switchTab(tabId));
  }, [dispatch]);

  const handleClose = useCallback((e, tabId) => {
    e.stopPropagation();
    dispatch(closeTab(tabId));
  }, [dispatch]);

  const handleContextMenu = useCallback((e, tab) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, tab });
  }, []);

  const handlePopOut = useCallback((tab) => {
    if (window.electronAPI?.openNewWindow) {
      window.electronAPI.openNewWindow(tab.path);
    } else {
      const url = window.location.origin + tab.path;
      window.open(url, "_blank", "width=1200,height=800,menubar=no,toolbar=no");
    }
    setContextMenu(null);
  }, []);

  const handleDragStart = useCallback((e, index) => {
    dragRef.current = index;
    e.dataTransfer.effectAllowed = "move";
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, []);

  const handleDrop = useCallback((e, toIndex) => {
    e.preventDefault();
    const fromIndex = dragRef.current;
    if (fromIndex !== null && fromIndex !== toIndex) {
      dispatch(reorderTabs({ fromIndex, toIndex }));
    }
    dragRef.current = null;
  }, [dispatch]);

  // Close context menu on click outside
  React.useEffect(() => {
    if (!contextMenu) return;
    const close = () => setContextMenu(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [contextMenu]);

  const isMini = document.body.classList.contains("mini-sidebar") && !document.body.classList.contains("expand-menu");

  if (tabs.length === 0) return null;

  return (
    <>
      <div style={{ ...styles.container, left: isMini ? 60 : 250 }}>
        <div style={styles.tabsScroll} className="bp-tab-scroll">
          {tabs.map((tab, index) => {
            const isActive = tab.id === activeTabId;
            return (
              <div
                key={tab.id}
                style={{
                  ...styles.tab(isActive),
                  ...(hoveredTab === tab.id && !isActive ? { background: "#eaeaea" } : {}),
                }}
                onClick={() => handleTabClick(tab.id)}
                onContextMenu={(e) => handleContextMenu(e, tab)}
                onMouseEnter={() => setHoveredTab(tab.id)}
                onMouseLeave={() => setHoveredTab(null)}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, index)}
                title={tab.title}
              >
                {/* Back/Forward arrows — only on active tab */}
                {isActive && (
                  <>
                    <span
                      style={canGoBack(tab.id) ? styles.tabIcon : styles.tabIconDisabled}
                      onClick={(e) => { e.stopPropagation(); goBack(tab.id); }}
                      title="Go back in this tab"
                    >
                      <FontAwesomeIcon icon={faArrowLeft} />
                    </span>
                    <span
                      style={canGoForward(tab.id) ? styles.tabIcon : styles.tabIconDisabled}
                      onClick={(e) => { e.stopPropagation(); goForward(tab.id); }}
                      title="Go forward in this tab"
                    >
                      <FontAwesomeIcon icon={faArrowRight} />
                    </span>
                  </>
                )}
                <span style={{ fontSize: "10px", color: isActive ? "#1677ff" : "#aaa", fontWeight: 700, flexShrink: 0 }}>{index + 1}</span>
                <span style={styles.tabTitle}>{tab.title}</span>
                {hoveredTab === tab.id && (
                  <span
                    style={styles.tabIcon}
                    onClick={(e) => { e.stopPropagation(); handlePopOut(tab); }}
                    title="Pop out to new window"
                  >
                    <FontAwesomeIcon icon={faExternalLinkAlt} />
                  </span>
                )}
                <span
                  style={{
                    ...styles.closeBtn,
                    ...(hoveredClose === tab.id ? { background: "#e0e0e0", color: "#333" } : {}),
                  }}
                  onClick={(e) => handleClose(e, tab.id)}
                  onMouseEnter={() => setHoveredClose(tab.id)}
                  onMouseLeave={() => setHoveredClose(null)}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <div style={{ ...styles.contextMenu, left: contextMenu.x, top: contextMenu.y }}>
          <div
            style={{ ...styles.contextItem, ...(hoveredCtx === "popout" ? { background: "#f5f5f5" } : {}) }}
            onClick={() => handlePopOut(contextMenu.tab)}
            onMouseEnter={() => setHoveredCtx("popout")}
            onMouseLeave={() => setHoveredCtx(null)}
          >
            <FontAwesomeIcon icon={faExternalLinkAlt} style={{ fontSize: "11px", color: "#888" }} />
            Open in New Window
          </div>
          <div
            style={{ ...styles.contextItem, ...(hoveredCtx === "close" ? { background: "#f5f5f5" } : {}) }}
            onClick={() => { dispatch(closeTab(contextMenu.tab.id)); setContextMenu(null); }}
            onMouseEnter={() => setHoveredCtx("close")}
            onMouseLeave={() => setHoveredCtx(null)}
          >
            <FontAwesomeIcon icon={faTimes} style={{ fontSize: "11px", color: "#888" }} />
            Close Tab
          </div>
          <div
            style={{ ...styles.contextItem, ...(hoveredCtx === "others" ? { background: "#f5f5f5" } : {}) }}
            onClick={() => { dispatch(closeOtherTabs(contextMenu.tab.id)); setContextMenu(null); }}
            onMouseEnter={() => setHoveredCtx("others")}
            onMouseLeave={() => setHoveredCtx(null)}
          >
            <FontAwesomeIcon icon={faWindowRestore} style={{ fontSize: "11px", color: "#888" }} />
            Close Other Tabs
          </div>
          <div
            style={{ ...styles.contextItem, ...(hoveredCtx === "all" ? { background: "#f5f5f5" } : {}) }}
            onClick={() => { dispatch(closeAllTabs()); setContextMenu(null); }}
            onMouseEnter={() => setHoveredCtx("all")}
            onMouseLeave={() => setHoveredCtx(null)}
          >
            <FontAwesomeIcon icon={faTimes} style={{ fontSize: "11px", color: "#888" }} />
            Close All Tabs
          </div>
        </div>
      )}

      {/* Hide scrollbar */}
      <style>{`.bp-tab-scroll::-webkit-scrollbar { display: none; }`}</style>
    </>
  );
};

export default TabBar;
